<template>
  <div class="d-flex">
    <v-card
      min-height="350"
      elevation="0" outlined
      v-if="!loading && game"
      class="d-flex flex-column h-100 flex-grow-1 justify-start"
      v-bind:class="{'rounded-r-0': xgMap}"
    >
      <v-card-title>
        <v-icon color="secondary" large class="mr-2">dvr</v-icon>
        <div class="d-flex flex-column" >
          <span
            class="primary--text monospace-font body-1 font-weight-medium"
          >{{$t("dashboards.lastGame")}}</span>
          <span
            v-if="game"
            class="subheader-font body-2 text-ellipsis"
            style="max-width:190px"
          >{{game.leagueName}} - {{game.matchDate}}</span>
        </div>
      </v-card-title>
      <div class="d-flex align-center justify-center mb-2">
        <div class="d-flex justify-end align-center mr-2 w-36">
          <div>
            <v-img
              :src="game.homeTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.homeTeamId + '.png'"
              :height="20"
              :width="20"
              class="mx-1"
              v-on:error="onImgError($event,'home')"
            />
          </div>
          <router-link
            :to="`/team/${game.homeTeamId}`"
            class="font-weight-medium body-2 text-ellipsis"
          >{{game.homeTeamName}}</router-link>
        </div>
        <router-link
          :to="`/game/${game.gameId}`"
          class="d-flex text-h6 font-weight-bold text-center black--text"
        >
          <span>{{game.homeTeamScore}}</span>
          <span class="mx-1">-</span>
          <span>{{game.awayTeamScore}}</span>
        </router-link>
        <div class="ml-2 d-flex align-center w-36">
          <router-link
            :to="`/team/${game.awayTeamId}`"
            class="font-weight-medium body-2 text-ellipsis"
          >{{game.awayTeamName}}</router-link>
          <div>
            <v-img
              :src="game.awayTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.awayTeamId + '.png'"
              :height="20"
              :width="20"
              class="mx-1"
              v-on:error="onImgError($event,'away')"
            />
          </div>
        </div>
      </div>
      <v-divider class="my-1"></v-divider>
      <div class="d-flex justify-center" v-if="loadingStats">
        <v-progress-circular :size="35" color="secondary" indeterminate v-if="loadingStats"></v-progress-circular>
      </div>
      <v-card-text v-if="homeStats && awayStats && !loadingStats">
        <div class="d-flex align-center justify-center py-1">
          <span
            class="primary--text w-12 text-center"
            v-if="homeStats"
          >{{homeStats.possession.toFixed(1)}}%</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.possession')}}</v-chip>
          <span
            class="primary--text w-12 text-center"
            v-if="awayStats"
          >{{awayStats.possession.toFixed(1)}}%</span>
        </div>
        <v-divider class="my-1"></v-divider>
        <div class="d-flex align-center justify-center py-1">
          <span class="primary--text w-10 text-center" v-if="homeStats">{{homeStats.shots}}</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.shots')}}</v-chip>
          <span class="primary--text w-10 text-center" v-if="awayStats">{{awayStats.shots}}</span>
        </div>
        <v-divider class="my-1"></v-divider>
        <div class="d-flex align-center justify-center py-1">
          <span class="primary--text w-10 text-center" v-if="homeStats">{{homeStats.xGP.toFixed(1)}}</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.xgExplicit')}}</v-chip>
          <span class="primary--text w-10 text-center" v-if="awayStats">{{awayStats.xGP.toFixed(1)}}</span>
        </div>
        <v-divider class="my-1"></v-divider>
        <div class="d-flex align-center justify-center py-1">
          <span
            class="primary--text w-10 text-center"
            v-if="homeStats"
          >{{homeStats.expectedPoints.toFixed(1)}}</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.expectedPoints')}}</v-chip>
          <span
            class="primary--text w-10 text-center"
            v-if="awayStats"
          >{{awayStats.expectedPoints.toFixed(1)}}</span>
        </div>
        <v-divider class="my-1"></v-divider>
        <div class="d-flex align-center justify-center py-1">
          <span class="primary--text w-10 text-center" v-if="homeStats">{{homeStats.ppda.toFixed(1)}}</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.ppda')}}</v-chip>
          <span class="primary--text w-10 text-center" v-if="awayStats">{{awayStats.ppda.toFixed(1)}}</span>
        </div>
        <v-divider class="my-1" v-if="showAllStats"></v-divider>
        <div class="d-flex align-center justify-center py-1" v-if="showAllStats && homeStats && awayStats && !loadingStats">
          <span class="primary--text w-10 text-center" v-if="homeStats">{{homeStats.directness.toFixed(1)}}</span>
          <v-chip
            outlined
            small
            :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary'"
            class="body-2"
          >{{$t('game.stats.directness')}}</v-chip>
          <span class="primary--text w-10 text-center" v-if="awayStats">{{awayStats.directness.toFixed(1)}}</span>
        </div>
      </v-card-text>
      <v-card-text class="d-flex flex-column align-center justify-center" v-if="!loadingStats && (!homeStats || !awayStats)">
        <v-icon color="primary lighten-5" v-bind:style="{'font-size':$vuetify.breakpoint.mdAndUp ? '80px' : '60px'}">blur_on</v-icon>
        <span class="primary--text lighten--text-2 body-1">{{$t("general.dataAvailable")}}</span>
      </v-card-text>
      <v-divider class="my-1"></v-divider>
      <div class="d-flex justify-end px-2 py-2">
        <v-btn
          color="secondary" class="elevation-0 monospace-font white--text px-5" rounded small
          :to="`/game/${game.gameId}`"
        >{{$t('dashboards.seeAllGameInfo')}}</v-btn>
      </div>
    </v-card>
    <v-card class="d-flex flex-grow-1 justify-center align-center py-8 px-12" v-if="loading">
      <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
    </v-card>
    <v-card
      v-if="xgMap"
      id="chart"
      ref="chart"
      class="text-center d-flex align-center rounded-l-0"
      elevation="2"
    >
      <XGChart
        class="mb-n2"
        v-if="game && xgMap && size != 0 && game != null && shots && shots.length > 0"
        :id="`${game.gameId}`"
        :size="size"
        :game="game"
        :shots="filteredShots"
        :key="xgMapKey"
        :homeTeamName="game.homeTeamName"
        :awayTeamName="game.awayTeamName"
        :circlesAndBoxes="checkboxCircles"
        :language="currentUserLanguage"
      />
    </v-card>
    <v-card elevation="0" outlined v-if="!loading && !game" class="d-flex flex-column flex-grow-1">
      <v-card-title>
        <v-icon color="secondary" large class="mr-2">dvr</v-icon>
        <div class="d-flex flex-column">
          <span
            class="primary--text monospace-font body-1 font-weight-medium"
          >{{$t("dashboards.lastGame")}}</span>
        </div>
      </v-card-title>
      <div class="d-flex justify-center align-center flex-column px-3 py-4 mb-13 text-center flex-grow-1">
        <v-icon color="primary lighten-5" v-bind:style="{'font-size':$vuetify.breakpoint.mdAndUp ? '80px' : '60px'}">event_busy</v-icon>
        <span class="primary--text lighten--text-2 body-1">{{$t("dashboards.placeholderNextGame")}}</span>
      </div>
    </v-card>
  </div>
</template>

<script>
import * as d3 from "d3";
import GameService from "../../../services/game/game.service";
import XGChart from "../../base/XGChart";
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: "LastGameWidget",
  components: {
    XGChart
  },
  props: {
    teamId: {
      type: Number,
      required: false
    },
    seasonId: {
      type: Number,
      required: false
    },
    xgMap: {
      type: Boolean,
      default: false
    },
    showAllStats: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    }
  },
  data() {
    return {
      game: null,
      homeStats: null,
      awayStats: null,
      loading: false,
      shots: null,
      filteredShots: null,
      checkboxCircles: ["circles"],
      size: 390,
      xgMapKey: 0,
      shotTypeModel:
        "${player.position}" == "GK" ? [15, 16] : [13, 14, 15, 16, 60],
      shotStrategyModel: [22, 23, 215, 24, 25, 26, 160, 123, 124],
      areaModel: [
        "goalArea",
        "boxLeft",
        "boxCenter",
        "boxRight",
        "outBoxDeepLeft",
        "outBoxDeepRight",
        "outBoxLeft",
        "outBoxCentreLeft",
        "outBoxCentre",
        "outBoxCentreRight",
        "outBoxRight",
        "left35",
        "centreLeft35",
        "centre35",
        "centreRight35",
        "right35"
      ],
      bodyPartModel: [15, 20, 21, 72],
      minutes: [0, 90],
      periods: ["period1", "period2"],
      sizeMultiplier: 1.47,
      observer: null,
      loadingStats: false
    };
  },
  filters: {
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
  },
  mounted() {
    this.size = this.showAllStats ? this.size * this.sizeMultiplier : this.size;
    this.loadViewData();
    this.observer = new ResizeObserver(this.onResize).observe(this.$el);
  },
  methods: {
    async loadViewData() {
      await this.loadGame();
      if (this.xgMap) {
        await this.loadShots();
      }
      this.loadGameSummaryStats();
    },
    async loadGame() {
      this.loading = true;
      if (this.teamId) {
        let gameResponse = await GameService.getLatestGamesPlayed(
          this.teamId,
          1
        );
        this.game = gameResponse.data[0];
      } else if (this.seasonId) {
        let gameResponse = await GameService.getLatestMatchdayGames(
          this.seasonId
        );
        this.game = _.sample(gameResponse.data);
      }
      this.loading = false;
    },
    async loadGameSummaryStats() {
      this.loadingStats = true;
      let gameResponse = await GameService.getGameSummaryStats(
        this.game.gameId
      );
      let stats = gameResponse.data;
      this.homeStats = _.find(stats, s => s.isHomeTeam);
      this.awayStats = _.find(stats, s => !s.isHomeTeam);
      this.loadingStats = false;
    },
    async loadShots() {
      let shotsResponse = await GameService.getXGShots(this.game.gameId);
      this.shots = shotsResponse.data;
      this.updateShots();
      this.onWindowResize();
    },
    onImgError(event, side) {
      if (side === "home") {
        this.game.homeTeamImgError = true;
      } else if (side === "away") {
        this.game.awayTeamImgError = true;
      }
    },
    shotsInArea(shots) {
      return shots
        .filter(
          s =>
            this.areaModel.includes("goalArea") ||
            !(s.x >= 94.2 && s.y >= 36.8 && s.y <= 63.2)
        )
        .filter(
          s =>
            this.areaModel.includes("boxRight") ||
            !(s.x >= 83 && s.y >= 21.1 && s.y < 36.8)
        )
        .filter(
          s =>
            this.areaModel.includes("boxCenter") ||
            !(s.x >= 83 && s.x < 94.2 && s.y >= 36.8 && s.y <= 63.2)
        )
        .filter(
          s =>
            this.areaModel.includes("boxLeft") ||
            !(s.x >= 83 && s.y > 63.2 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxDeepRight") ||
            !(s.x >= 83 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxDeepLeft") ||
            !(s.x >= 83 && s.y > 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxRight") ||
            !(s.x >= 65 && s.x < 83 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentreRight") ||
            !(s.x >= 65 && s.x < 83 && s.y >= 21.1 && s.y <= 35.5)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentre") ||
            !(s.x >= 65 && s.x < 83 && s.y > 35.5 && s.y <= 64.4)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentreLeft") ||
            !(s.x >= 65 && s.x < 83 && s.y > 64.4 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxLeft") ||
            !(s.x >= 65 && s.x < 83 && s.y > 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("right35") ||
            !(s.x >= 50 && s.x < 65 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("centreRight35") ||
            !(s.x >= 50 && s.x < 65 && s.y >= 21.1 && s.y <= 35.5)
        )
        .filter(
          s =>
            this.areaModel.includes("centre35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 35.5 && s.y <= 64.4)
        )
        .filter(
          s =>
            this.areaModel.includes("centreLeft35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 64.4 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("left35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 78.9)
        );
    },
    updateShots() {
      var self = this;

      var minutesMaxFilter = this.minutes[1] === 90 ? 150 : this.minutes[1];

      this.filteredShots = this.shots
        .filter(s =>
          self.shotTypeModel.includes(s.eventTypeId)
        )
        .filter(s =>
          self.shotStrategyModel.includes(
            s.strategyId
          )
        )
        .filter(s =>
          self.bodyPartModel.includes(s.bodyPartId)
        )
        .filter(s => _.some(self.periods, p => p.includes(s.periodId)))
        .filter(s => s.min >= self.minutes[0] && s.min < minutesMaxFilter);

      this.filteredShots = this.shotsInArea(this.filteredShots);

      this.xgMapKey += 1;
    },
    onWindowResize(e) {
      if (this.$refs.chart && this.size != this.$refs.chart.clientWidth * this.sizeMultiplier) {
        this.xgMapKey += 1;
      }
    },
    onResize() {
      this.$emit("heightChange", this.$el.clientHeight);
    }
  },
  destroyed() {
    d3.selectAll(".tooltip").remove();
    window.removeEventListener("resize", this.onWindowResize);
    delete this.observer;
  }
};
</script>



// WEBPACK FOOTER //
// src/views/dashboard/widgets/LastGameWidget.vue